import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Check for session_id in URL fragment
    const fragment = window.location.hash;
    const sessionIdMatch = fragment.match(/session_id=([^&]+)/);

    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      handleGoogleAuth(sessionId);
      // Clean URL
      window.location.hash = '';
      return;
    }

    // Check existing authentication
    checkAuth();
  }, []);

  const handleGoogleAuth = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/auth/session`, {
        headers: { 'X-Session-ID': sessionId }
      });

      const { user, session_token } = response.data;
      setUser(user);
      setToken(session_token);
      localStorage.setItem('token', session_token);
      
      // Set cookie
      document.cookie = `session_token=${session_token}; path=/; max-age=${7 * 24 * 60 * 60}; secure; samesite=none`;
      
      setLoading(false);
    } catch (error) {
      console.error('Google auth error:', error);
      setLoading(false);
    }
  };

  const checkAuth = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    setUser(userData);
    setToken(newToken);
    localStorage.setItem('token', newToken);
    
    return userData;
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    
    setUser(userData);
    setToken(newToken);
    localStorage.setItem('token', newToken);
    
    return userData;
  };

  const registerFounder = async (code, email, password, name, role, intake) => {
    const body = { code, email, password, name, role };
    if (intake) body.intake = intake;
    const response = await axios.post(`${API}/auth/founders/register`, body);
    const { token: newToken, user: userData } = response.data;

    setUser(userData);
    setToken(newToken);
    localStorage.setItem('token', newToken);

    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    document.cookie = 'session_token=; path=/; max-age=0';
  };

  const googleLogin = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, registerFounder, logout, googleLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);